import { useEffect, useState, useRef } from 'react';
import { Outlet, useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { verifyAdminUrl, getUsers, getAdminSettings } from '@/lib/api';
import ProcessTracker from '@/components/ProcessTracker';
import NotificationBell from '@/components/NotificationBell';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import { 
  LayoutDashboard, 
  Users, 
  Phone, 
  AlertTriangle, 
  Cloud,
  ChevronRight,
  Menu,
  X,
  FolderOpen,
  Activity,
  UserCircle,
  Search,
  Eye,
  ChevronDown,
  ArrowLeft,
  BookOpen,
  Settings,
  FileBarChart,
  GraduationCap,
  Trophy,
  FileText,
  UserSearch,
  HelpCircle,
  ClipboardCheck,
  Target,
  MessageCircle,
  FileAudio,
  Trash2,
  Send,
  Shield,
  UserCog
} from 'lucide-react';
import AppTour, { ADMIN_TOUR_STEPS } from '@/components/AppTour';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/ThemeToggle';

const ICON_MAP = {
  LayoutDashboard,
  Users,
  UserCircle,
  Phone,
  UserSearch,
  AlertTriangle,
  FileBarChart,
  GraduationCap,
  ClipboardCheck,
  Trophy,
  FolderOpen,
  BookOpen,
  Activity,
  FileText,
  Settings,
  Cloud,
  Library: FolderOpen,
  Target,
  MessageCircle,
  FileAudio,
  Trash2,
  Send,
  Shield,
  UserCog,
};

// Logo URL
const INSYRA_LOGO = "/insyra-logo.png";

const DEFAULT_NAV_ITEMS = [
  { id: 'dashboard', icon: 'LayoutDashboard', label: 'Dashboard', path: '' },
  { id: 'users', icon: 'Users', label: 'Users', path: 'users' },
  { id: 'user-session', icon: 'UserCircle', label: 'User Session', path: 'user-session' },
  { id: 'calls', icon: 'Phone', label: 'Calls', path: 'calls' },
  { id: 'search-applicants', icon: 'UserSearch', label: 'Search Applicants', path: 'search-applicants' },
  { id: 'alerts', icon: 'AlertTriangle', label: 'Alerts', path: 'alerts' },
  { id: 'auditors-dashboard', icon: 'Shield', label: 'Auditors Dashboard', path: 'auditors-dashboard' },
  { id: 'reports', icon: 'FileBarChart', label: 'Notes Compliance', path: 'reports' },
  { id: 'kpi', icon: 'Target', label: 'KPI', path: 'kpi' },
  { id: 'review-requests', icon: 'Send', label: 'Review Requests', path: 'review-requests' },
  { id: 'ai-coach', icon: 'MessageCircle', label: 'AI Communication Coach', path: 'ai-coach' },
  { id: 'library', icon: 'Library', label: 'Library', path: 'library' },
  { id: 'leaderboard', icon: 'Trophy', label: 'Leaderboard', path: 'leaderboard' },
  { id: 'repository', icon: 'FolderOpen', label: 'Repository', path: 'repository' },
  { id: 'scoring-guide', icon: 'BookOpen', label: 'Scoring Guide', path: 'scoring-guide' },
  { id: 'team', icon: 'Users', label: 'Know the Team', path: 'team' },
  { id: 'support', icon: 'MessageCircle', label: 'Support Center', path: 'support' },
  { id: 'activity', icon: 'Activity', label: 'Activity Log', path: 'activity' },
  { id: 'documentation', icon: 'FileText', label: 'App Documentation', path: 'documentation' },
  { id: 'settings', icon: 'Settings', label: 'Settings', path: 'settings' },
  { id: 'audio-settings', icon: 'FileAudio', label: 'Audio & Call Settings', path: 'audio-settings', restrictedFeature: 'audio_settings' },
  { id: 'onedrive', icon: 'Cloud', label: 'OneDrive', path: 'onedrive', restrictedFeature: 'onedrive' },
  { id: 'master-delete', icon: 'Trash2', label: 'Master Delete', path: 'master-delete', restrictedFeature: 'master_delete' },
];

export default function AdminLayout() {
  const { adminUrl } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navItems, setNavItems] = useState(DEFAULT_NAV_ITEMS);
  
  // User Session state
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Tour state
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        await verifyAdminUrl(adminUrl);
        setIsValid(true);
        // Load users for the session selector
        const usersRes = await getUsers();
        setUsers(usersRes.data);
        
        // Load custom nav order
        try {
          const settingsRes = await getAdminSettings();
          if (settingsRes.data.nav_order && settingsRes.data.nav_order.length > 0) {
            const orderedItems = settingsRes.data.nav_order
              .map(id => DEFAULT_NAV_ITEMS.find(item => item.id === id))
              .filter(Boolean);
            const savedIds = new Set(settingsRes.data.nav_order);
            const newItems = DEFAULT_NAV_ITEMS.filter(item => !savedIds.has(item.id));
            setNavItems([...orderedItems, ...newItems]);
          }
        } catch (e) {
          // Use default order if settings fetch fails
        }
        
        // Check if tour has been completed
        const tourCompleted = localStorage.getItem('admin_tour_completed');
        if (!tourCompleted) {
          setTimeout(() => {
            setShowTour(true);
          }, 500);
        }
      } catch (error) {
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    verify();
  }, [adminUrl, navigate]);

  const handleExitUserView = () => {
    setSelectedUser(null);
    navigate(`/admin/${adminUrl}`);
  };

  const handleStartTour = () => {
    setShowTour(true);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-muted/50">
        <div className="w-64 bg-slate-900 p-4">
          <Skeleton className="h-8 w-32 mb-8 bg-slate-800" />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full mb-2 bg-slate-800" />
          ))}
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="flex h-screen bg-muted/50 dark:bg-slate-900 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-sm text-muted-foreground dark:text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  const currentPath = location.pathname.split('/').pop();

  return (
    <div className="flex h-screen bg-muted/50 dark:bg-slate-900">
      {/* App Tour */}
      {showTour && (
        <AppTour 
          steps={ADMIN_TOUR_STEPS}
          onComplete={() => setShowTour(false)}
          isAdmin={true}
        />
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-slate-900 transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="absolute inset-0 sidebar-texture pointer-events-none" />
        
        <div className="relative h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={INSYRA_LOGO} alt="Insyra AI" className="h-10 w-auto" />
                <div>
                  <p className="text-xs text-muted-foreground mt-1">Voice → Insight → Impact</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-muted-foreground"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = currentPath === item.path || 
                (item.path === '' && currentPath === adminUrl);
              const IconComponent = ICON_MAP[item.icon] || Settings;
              
              return (
                <Link
                  key={item.id || item.path}
                  to={`/admin/${adminUrl}/${item.path}`}
                  className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <IconComponent className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
            
            {/* Tour Button */}
            <button
              onClick={handleStartTour}
              className="sidebar-item w-full text-left mt-4 border-t border-slate-800 pt-4"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="font-medium">Take a Tour</span>
            </button>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-slate-800">
            <div className="px-4 py-3 bg-slate-800/50 rounded-sm">
              <p className="text-xs text-muted-foreground">Admin Portal</p>
              <p className="text-sm text-slate-300 font-medium truncate mt-1">
                {adminUrl.slice(0, 12)}...
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            <h2 className="text-lg font-semibold text-foreground font-heading">
              {navItems.find(n => n.path === currentPath || (n.path === '' && currentPath === adminUrl))?.label || 'Dashboard'}
            </h2>
          </div>
          
          {/* User Session Indicator (when active) */}
          {selectedUser && (
            <div className="flex items-center gap-3 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
              <Eye className="h-4 w-4 text-blue-600" />
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={selectedUser.avatar_url} />
                  <AvatarFallback className="bg-blue-200 text-blue-700 text-xs">
                    {selectedUser.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-blue-700">
                  Viewing as: {selectedUser.name}
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleExitUserView}
                className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Exit
              </Button>
            </div>
          )}

          {/* Tour button in header */}
          {!selectedUser && (
            <div className="flex items-center gap-2">
              <WorkspaceSwitcher />
              <NotificationBell 
                userId={adminInfo?.admin_id} 
                userRole="admin" 
                tenantId={adminInfo?.tenant_id}
              />
              <ThemeToggle variant="icon" />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleStartTour}
                className="text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-slate-200"
              >
                <HelpCircle className="h-4 w-4 mr-1" />
                Tour
              </Button>
            </div>
          )}
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto p-6 lg:p-8">
          <Outlet context={{ adminUrl, selectedUser, setSelectedUser, users, isUserSession: !!selectedUser, tenantId: adminInfo?.tenant_id }} />
        </div>
        
        {/* Process Tracker */}
        <ProcessTracker />
      </main>
    </div>
  );
}
